import { ChecklistenEingabe } from '../components/ChecklistenEingabe'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { PageHeader, Card, Badge, Tabs, TabsList, TabsTrigger, TabsContent, useEinsatz, RequireEinsatz, useConnectionRetry, useBestaetigung, aktuellePhase, phaseIndex, phaseLabel, offeneEinsatzChecklisten, istBeendetOderAbgeschlossen, PhasenZeitenDialog, type EinsatzPhaseKey } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { checklistenTitel } from '../components/checklistenTitel'
import { drohnenWind } from '../utils/drohnenWind'
import { FlugAufnahme } from '../components/FlugAufnahme'
import { FreigabeLinkButton } from '../components/FreigabeLinkDialog'
import { PhasenZeitleiste } from '../components/PhasenZeitleiste'
import { usePhasenSetzen, PHASE_GEAENDERT } from '../components/PhasenSteuerung'
import {
  Users, Plane, Clock, MapPin, Radio, Zap,
  Truck, Activity, Truck as TruckIcon, UserCheck, Shield,
  BarChart2, AlertTriangle, Clock as ClockIcon,
  ClipboardList, CheckSquare, Loader2, ChevronLeft, ChevronRight,
  CheckCircle, Thermometer, Droplets, Wind, Cloud, Sun,
  Eye, Compass, Gauge, Maximize2, ChevronDown, X, Navigation, PlaneTakeoff, Satellite, BatteryMedium, TriangleAlert
} from 'lucide-react'

/** Wert eines Zahlenfelds: nur Zahlen anzeigen (ältere Einträge enthalten "true" aus „Alle Pflichtfelder ausfüllen“) */
const zahlWert = (w: string | null | undefined) => (w && Number.isFinite(Number(w)) ? w : '')

// ============================================================================
// Types
// ============================================================================

interface EinsatzOption {
  id: number
  titel: string
  status: string
  typ: string
  beginn_geplant: string | null
  einsatzort_lat?: number | null
  einsatzort_lon?: number | null
  einsatzort_adresse?: string | null
}

interface EinsatzKrafte {
  im_einsatz: number
  reserve_wache: number
  einsatzreserve: number
  detail_im_einsatz: any[]
  detail_reserve_wache: any[]
  detail_einsatzreserve: any[]
}

interface Piloten {
  eingeteilt: number
  verfuegbar: number
  detail_eingeteilt: any[]
  detail_verfuegbar: any[]
}

interface Fluege {
  anzahl: number
  flugzeit_minuten: number
  flugzeit_label: string
}

interface GeflogenerPilot {
  nutzer_id: number
  nutzer_label: string
  flug_anzahl: number
  flugzeit_minuten: number
  flugzeit_label: string
}

interface Einsatzleitung {
  einsatzleiter_typ: string
  einsatzleiter_nutzer_id: number | null
  einsatzleiter_name: string | null
  einsatzleiter_funkrufname: string | null
  einsatzleiter_funkgruppe: string | null
}

interface Einsatzdauer {
  minuten: number
  stunden: number
  minuten_rest: number
  label: string
}

interface Fahrzeug {
  fahrzeug_id: number
  label: string
  nutzer_label: string | null
  funktion_name: string | null
}

interface FlugListe {
  id: number
  startzeit: string | null
  endzeit: string | null
  dauer_minuten: number
  pilot_label: string | null
  beobachter_label: string | null
  drohne_label: string | null
  akku_label: string | null
  startort: string | null
  zielort: string | null
}

interface ChecklistenItem {
  id: number
  text: string
  typ: string
  pflicht: boolean
  sortierung: number
  hinweis: string
  wert: string | null
  ausgefuellt_am: string | null
  ausgefuellt_von: number | null
}

interface ChecklistenInstanz {
  id: number
  name: string
  phase: string
  modul: string
  status: string
  erstellt_am: string
  abgeschlossen_am: string | null
  abgeschlossen_von: number | null
  flug_id?: number | null
  /** Namen derer, die Punkte ausgefüllt haben (vom Server) */
  bearbeiter?: string[]
  items: ChecklistenItem[]
}


interface WeatherData {
  temperature: number
  dewpoint: number
  windspeed: number
  windgusts: number
  winddirection: number
  precipitation_probability: number
  cloudcover: number
  visibility: number
  uv_index: number
  /** geomagnetischer Kp-Index (NOAA SWPC); null, wenn nicht abrufbar */
  kp_index: number | null
  time: string
  /** Datenquelle vom Server: 'open-meteo' | 'brightsky' (DWD) | 'cache-veraltet' */
  quelle?: string
  /** Zeitpunkt der Abfrage beim Anbieter (ISO) */
  stand?: string
}

interface DashboardData {
  einsatz: { id: number; titel: string; typ: string; status: string; phase?: string | null; aktuelle_phase?: string | null; alarmierungszeit?: string | null; abfahrt_zeit?: string | null; ankunft_zeit?: string | null; einsatzende_zeit?: string | null; einsatzbereit_zeit?: string | null; beginn_geplant?: string | null; einsatzort_lat?: number | null; einsatzort_lon?: number | null; einsatzort_adresse?: string | null; einsatznummer?: string | null; ergebnis?: string | null; ergebnis_person?: string | null }
  aktive_einsaetze: EinsatzOption[]
  einsatzkraefte: EinsatzKrafte
  piloten: Piloten
  fluege: Fluege
  geflogene_piloten: GeflogenerPilot[]
  einsatzleitung: Einsatzleitung
  einsatzdauer: Einsatzdauer
  fahrzeuge: Fahrzeug[]
  fluege_liste: FlugListe[]
}

const statusVariant = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'ALARMIERUNG': case 'ABFAHRT_WACHE': return 'warning'
    case 'ANKUNFT_EINSATZORT': return 'info'
    case 'LAUFEND': case 'AKTIV': return 'danger'
    case 'GEPLANT': return 'info'
    case 'BEENDET': case 'EINSATZENDE': case 'EINSATZBEREIT': case 'ABGESCHLOSSEN': return 'success'
    default: return 'info'
  }
}

const fmtTime = (s: string | null) => s ? new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '—'

// ============================================================================
// Components
// ============================================================================

function EinsatzKrafteCard({ data, className }: { data?: EinsatzKrafte; className?: string }) {
  return (
    <Card className={`p-3 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Einsatzkräfte</h3>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Im Einsatz</span>
          </div>
          <span className="text-xl font-bold text-dark-text">{data?.im_einsatz ?? 0}</span>
        </div>
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex min-w-0 items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-dark-text-muted" />
            <span className="min-w-0 text-sm font-medium text-dark-text">Reserve Wache / Bereithalteort</span>
          </div>
          <span className="ml-3 shrink-0 text-xl font-bold text-dark-text">{data?.reserve_wache ?? 0}</span>
        </div>
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Einsatzreserve</span>
          </div>
          <span className="text-xl font-bold text-dark-text">{data?.einsatzreserve ?? 0}</span>
        </div>
      </div>
    </Card>
  )
}

function PilotenCard({ data, className }: { data?: Piloten; className?: string }) {
  return (
    <Card className={`p-3 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Plane className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Piloten</h3>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Eingeteilte Piloten</span>
          </div>
          <span className="text-xl font-bold text-dark-text">{data?.eingeteilt ?? 0}</span>
        </div>
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Verfügbare Piloten</span>
          </div>
          <span className="text-xl font-bold text-dark-text">{data?.verfuegbar ?? 0}</span>
        </div>
      </div>
    </Card>
  )
}

function FluegeCard({ data, className }: { data?: Fluege; className?: string }) {
  return (
    <Card className={`p-3 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Plane className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Flüge</h3>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Anzahl Flüge</span>
          </div>
          <span className="text-xl font-bold text-dark-text">{data?.anzahl ?? 0}</span>
        </div>
        <div className="flex items-center justify-between py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Gesamtflugzeit</span>
          </div>
          <span className="whitespace-nowrap text-xl font-bold text-dark-text">{data?.flugzeit_label ?? '—'}</span>
        </div>
      </div>
    </Card>
  )
}

function GeflogenePilotenCard({ data, className }: { data?: GeflogenerPilot[]; className?: string }) {
  return (
    <Card className={`p-4 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-4">
        <BarChart2 className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Geflogene Piloten</h3>
      </div>
      {(data?.length ?? 0) === 0 ? (
        <p className="text-center py-6 text-sm text-dark-text-muted">Noch keine Flüge durchgeführt</p>
      ) : (
        <div className="space-y-2">
          {(data ?? []).map((p, i) => (
            <div key={i} className="flex items-center justify-between py-2 px-3 rounded bg-dark-bg-tertiary/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary-600/20 flex items-center justify-center text-primary-400 font-medium text-sm">
                  {p.nutzer_label?.split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-dark-text truncate max-w-[180px]">{p.nutzer_label}</p>
                  <p className="text-xs text-dark-text-muted">{p.flug_anzahl} {p.flug_anzahl === 1 ? 'Flug' : 'Flüge'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-dark-text">{p.flugzeit_label}</p>
                <p className="text-xs text-dark-text-muted">Flugzeit</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function EinsatzleitungCard({ data, className }: { data?: Einsatzleitung; className?: string }) {
  return (
    <Card className={`p-3 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Radio className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Einsatzleitung</h3>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3 py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex shrink-0 items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Typ</span>
          </div>
          <span className="min-w-0 truncate text-right text-base font-medium text-dark-text" title={data?.einsatzleiter_typ || ''}>{data?.einsatzleiter_typ || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex shrink-0 items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Name</span>
          </div>
          <span className="min-w-0 truncate text-right text-base font-medium text-dark-text" title={data?.einsatzleiter_name || ''}>{data?.einsatzleiter_name || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex shrink-0 items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Funkrufname</span>
          </div>
          <span className="min-w-0 truncate text-right text-base font-medium text-dark-text font-mono" title={data?.einsatzleiter_funkrufname || ''}>{data?.einsatzleiter_funkrufname || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-1 px-3 rounded bg-dark-bg-tertiary/50">
          <div className="flex shrink-0 items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-dark-text-muted" />
            <span className="text-sm font-medium text-dark-text">Funkgruppe</span>
          </div>
          <span className="min-w-0 truncate text-right text-base font-medium text-dark-text font-mono" title={data?.einsatzleiter_funkgruppe || ''}>{data?.einsatzleiter_funkgruppe || '—'}</span>
        </div>
      </div>
    </Card>
  )
}

/** Dauer > 24 h als „21 Tage 18 h“, sonst „5h 12min“ */
function dauerLabel(d?: Einsatzdauer): string {
  if (!d) return '—'
  const min = typeof d.minuten === 'number' ? d.minuten : null
  if (min == null || min < 24 * 60) return d.label ?? '—'
  const tage = Math.floor(min / 1440)
  const std = Math.floor((min % 1440) / 60)
  return `${tage} ${tage === 1 ? 'Tag' : 'Tage'} ${std} h`
}

// Wetter-Warnschwellen (Feedback #1): Kachel wird rot, wenn der Wert strikt größer ist
export const BOEEN_ROT_MS = 12   // Böen in m/s (Open-Meteo liefert km/h -> Umrechnung durch 3,6)
export const KP_GELB = 4         // geomagnetischer Kp-Index: ab 4 gelb
export const KP_ROT = 5          // ab 5 rot
export const boeenRot = (kmh: number | null | undefined) => kmh != null && Math.round((kmh / 3.6) * 100) / 100 > BOEEN_ROT_MS
export const kpRot = (kp: number | null | undefined) => kp != null && kp >= KP_ROT
export const kpGelb = (kp: number | null | undefined) => kp != null && kp >= KP_GELB && kp < KP_ROT
const KACHEL_NORMAL = 'p-3 rounded-lg bg-dark-bg-tertiary/50'
const KACHEL_ROT = 'p-3 rounded-lg border border-red-500/60 bg-red-500/15'
const KACHEL_GELB = 'p-3 rounded-lg border border-yellow-500/60 bg-yellow-500/15'

function WeatherCard({ data, className, einsatzId }: { data: WeatherData | null; className?: string; einsatzId?: string | null }) {
  if (!data) {
    return (
      <Card className={`p-4 ${className || ''}`}>
        <div className="flex items-center gap-2 mb-4">
          <Sun className="h-5 w-5 text-yellow-400" />
          <h3 className="font-semibold text-dark-text">Wetter</h3>
        </div>
        <div className="text-center py-8 text-dark-text-muted">Lade Wetterdaten…</div>
      </Card>
    )
  }
  
  const windDir = data.winddirection
  const windDirText = windDir == null ? '—' : [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
  ][Math.round(windDir / 22.5) % 16]
  
  return (
    <Card className={`p-4 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-4">
        <Sun className="h-5 w-5 text-yellow-400" />
        <h3 className="font-semibold text-dark-text">Wetter (lokal)</h3>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Thermometer className="h-4 w-4" />
              <span>Temp.</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.temperature.toFixed(1)}°C</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Droplets className="h-4 w-4" />
              <span>Taupunkt</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.dewpoint != null ? data.dewpoint.toFixed(1) : '—'}°C</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Wind className="h-4 w-4" />
              <span>Wind</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.windspeed != null ? data.windspeed.toFixed(1) : '—'} km/h</div>
          </div>
          <div className={boeenRot(data.windgusts) ? KACHEL_ROT : KACHEL_NORMAL} data-testid="wetter-boeen" data-rot={boeenRot(data.windgusts)}>
            <div className={`flex items-center gap-2 text-xs ${boeenRot(data.windgusts) ? 'text-red-300' : 'text-dark-text-muted'}`}>
              <Zap className="h-4 w-4" />
              <span>Böen</span>
            </div>
            <div className={`text-base font-bold [overflow-wrap:anywhere] ${boeenRot(data.windgusts) ? 'text-red-400' : 'text-dark-text'}`}
              title="Rot ab Böen über 12 m/s (43,2 km/h)">{data.windgusts != null ? data.windgusts.toFixed(1) : '—'} km/h</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Compass className="h-4 w-4" />
              <span>Richtung</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{windDir != null ? `${windDirText} (${windDir}°)` : '—'}</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Cloud className="h-4 w-4" />
              <span>Bewölkung</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.cloudcover != null ? `${data.cloudcover}%` : '—'}</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Droplets className="h-4 w-4" />
              <span>Niederschlag</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.precipitation_probability != null ? `${data.precipitation_probability}%` : '—'}</div>
          </div>
          <div className="min-w-0 p-2 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Eye className="h-4 w-4" />
              <span>Sichtweite</span>
            </div>
            <div className="text-base font-bold [overflow-wrap:anywhere] text-dark-text">{data.visibility != null ? `${data.visibility} km` : '—'}</div>
          </div>
          <div className={kpRot(data.kp_index) ? KACHEL_ROT : kpGelb(data.kp_index) ? KACHEL_GELB : KACHEL_NORMAL} data-testid="wetter-kp" data-rot={kpRot(data.kp_index)} data-gelb={kpGelb(data.kp_index)}>
            <div className={`flex items-center gap-2 text-xs ${kpRot(data.kp_index) ? 'text-red-300' : kpGelb(data.kp_index) ? 'text-yellow-300' : 'text-dark-text-muted'}`}>
              <Gauge className="h-4 w-4" />
              <span>Kp-Index</span>
            </div>
            <div className={`text-base font-bold [overflow-wrap:anywhere] ${kpRot(data.kp_index) ? 'text-red-400' : kpGelb(data.kp_index) ? 'text-yellow-400' : 'text-dark-text'}`}
              title="Geomagnetische Aktivität (NOAA). Gelb ab Kp 4, rot ab Kp 5; ab Kp 5 kann die GNSS-Genauigkeit der Drohne leiden.">
              {data.kp_index != null ? data.kp_index.toFixed(1).replace('.', ',') : '—'}
            </div>
          </div>
        </div>
        {einsatzId && <DrohnenWindKachel einsatzId={einsatzId} />}
        <p className="text-xs text-dark-text-muted text-center" data-testid="wetter-stand">
          Stand: {new Date(data.time).toLocaleString('de-DE')}
          {data.quelle === 'brightsky' && ' · Quelle DWD'}
          {data.quelle === 'cache-veraltet' && data.stand && ` · veraltet (abgerufen ${new Date(data.stand).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })})`}
        </p>
      </div>
    </Card>
  )
}

/** Wind am Fluggerät (geschätzt aus der Drohnen-Telemetrie) – nur sichtbar, wenn eine Drohne des Einsatzes aktuell sendet.
 *  Mehrere Drohnen: die erste mit frischen Windwerten (aktive-fluege ist bereits auf den gewählten Einsatz begrenzt). */
function DrohnenWindKachel({ einsatzId }: { einsatzId: string }) {
  const [wind, setWind] = useState<{ ms: number; kmh: number; richtung: string | null; drohne: string | null } | null>(null)
  useEffect(() => {
    let aktiv = true
    const holen = () => {
      if (document.visibilityState !== 'visible') return
      api.get(`/dashboard/einsatz/${einsatzId}/aktive-fluege`).then(r => {
        if (!aktiv) return
        for (const f of (r.data as AktiverFlug[]) ?? []) {
          const w = f.telemetrie && f.alter_s != null && f.alter_s <= 10 ? drohnenWind(f.daten) : null
          if (w) { setWind({ ...w, drohne: f.drohne }); return }
        }
        setWind(null)
      }).catch(() => { /* Wetter bleibt unabhängig von der Drohnen-Abfrage */ })
    }
    setWind(null)
    holen()
    const t = window.setInterval(holen, 5000)
    return () => { aktiv = false; window.clearInterval(t) }
  }, [einsatzId])
  if (!wind) return null
  return (
    <div className="p-2 rounded-lg bg-dark-bg-tertiary/50" data-testid="wetter-drohnenwind">
      <div className="flex items-center gap-2 text-dark-text-muted text-xs">
        <Wind className="h-4 w-4" />
        <span>Wind am Fluggerät (geschätzt, Drohne{wind.drohne ? ` ${wind.drohne}` : ''})</span>
      </div>
      <div className="text-base font-bold text-dark-text">
        {wind.kmh.toFixed(1).replace('.', ',')} km/h ({wind.ms.toFixed(1).replace('.', ',')} m/s){wind.richtung ? ` · ${wind.richtung}` : ''}
      </div>
    </div>
  )
}

function FahrzeugeListe({ data }: { data?: Fahrzeug[] }) {
  // Deduplicate by fahrzeug_id - take first entry per vehicle
  const uniqueFahrzeuge = Object.values(
    (data ?? []).reduce((acc, f) => {
      if (!acc[f.fahrzeug_id] || (f.nutzer_label && !acc[f.fahrzeug_id].nutzer_label)) {
        acc[f.fahrzeug_id] = f;
      }
      return acc;
    }, {} as Record<number, Fahrzeug>)
  );

  return (
    <Card className="h-full p-4">
      <div className="flex items-center gap-2 mb-4">
        <TruckIcon className="w-5 h-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Eingesetzte Fahrzeuge ({uniqueFahrzeuge.length})</h3>
      </div>
      {uniqueFahrzeuge.length === 0 ? (
        <p className="text-center py-8 text-dark-text-muted">Keine Fahrzeuge zugewiesen</p>
      ) : (
        <div className="space-y-2">
          {uniqueFahrzeuge.map((f, i) => (
            <div key={f.fahrzeug_id} className="flex items-center justify-between py-2 px-3 rounded bg-dark-bg-tertiary/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
                  <TruckIcon className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-dark-text">{f.label}</p>
                  <p className="text-xs text-dark-text-muted">{f.nutzer_label} · {f.funktion_name}</p>
                </div>
              </div>
              <Badge variant="info" className="text-xs">{f.funktion_name}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Checklisten Phase Cards for Dashboard
// ============================================================================

// Piloten-Checklisten (je Flug und Einsatzende Pilot) sind nur im Piloten-Modul sichtbar

type ChecklistenAktionen = {
  onUpdateItem: (instanzId: number, itemId: number, wert: string) => void
  onUpdateInstanzStatus: (instanzId: number, status: string) => void
}

function checklistenStand(instanz: ChecklistenInstanz) {
  const pflichtItems = instanz.items.filter(i => i.pflicht)
  const erledigt = pflichtItems.filter(i => i.wert && i.wert !== '').length
  const gesamt = pflichtItems.length
  return {
    erledigt, gesamt,
    isComplete: gesamt > 0 && erledigt === gesamt,
    isInProgress: gesamt > 0 && erledigt > 0 && erledigt < gesamt,
    isCompleted: instanz.status === 'ABGESCHLOSSEN',
  }
}

/** Fortschritt „x/y Pflicht“ und Haken bei abgeschlossener Checkliste */
function ChecklistenFortschritt({ instanz }: { instanz: ChecklistenInstanz }) {
  const { erledigt, gesamt, isComplete, isInProgress } = checklistenStand(instanz)
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Badge variant={isComplete ? 'success' : isInProgress ? 'warning' : 'danger'}>
        {erledigt}/{gesamt} Pflicht
      </Badge>
      {instanz.status === 'ABGESCHLOSSEN' && (
        <Badge variant="success" className="bg-green-600 text-white" title="Abgeschlossen">
          <CheckCircle className="w-4 h-4" />
        </Badge>
      )}
    </div>
  )
}

/** Ganze Zeile antippbar (label), solange es ein Haken-Punkt ist */
function ChecklistenZeile({ istCheck, className, children }: { istCheck: boolean; className: string; children: ReactNode }) {
  return istCheck
    ? <label className={`${className} block cursor-pointer`}>{children}</label>
    : <div className={className}>{children}</div>
}

/** Punkte und Aktionen einer Checkliste – in der Karte kompakt, in der Großansicht mit größerer Schrift/Bedienelementen */
function ChecklistenInhalt({ instanz, onUpdateItem, onUpdateInstanzStatus, gross = false }: ChecklistenAktionen & {
  instanz: ChecklistenInstanz
  gross?: boolean
}) {
  const { erledigt, gesamt, isCompleted } = checklistenStand(instanz)
  const feld = gross ? 'text-base py-2' : 'text-sm py-1.5'
  return (
    <div className={gross ? 'space-y-3' : 'space-y-2'}>
      {instanz.items.map(item => (
        <ChecklistenZeile key={item.id} istCheck={item.typ === 'CHECK' && !isCompleted} className={`${gross ? 'p-4' : 'p-3'} min-h-[44px] rounded-lg border border-dark-border bg-dark-bg-tertiary/50`}>
          <div className="flex items-start gap-3">
            {item.typ === 'CHECK' && (
              <input
                type="checkbox"
                checked={item.wert === 'true'}
                onChange={(e) => onUpdateItem(instanz.id, item.id, e.target.checked ? 'true' : '')}
                className={`${gross ? 'mt-0.5 h-6 w-6' : 'mt-1 h-5 w-5'} cursor-pointer shrink-0 rounded border-dark-border text-primary-500 focus:ring-primary-500`}
                disabled={isCompleted}
              />
            )}
            {item.typ === 'TEXT' && (
              <ChecklistenEingabe
                type="text"
                wert={item.wert || ''}
                onSpeichern={(v) => onUpdateItem(instanz.id, item.id, v)}
                className={`min-w-0 flex-1 px-3 ${feld} rounded border border-dark-border bg-dark-bg text-dark-text`}
                placeholder="Freitext..."
                disabled={isCompleted}
              />
            )}
            {item.typ === 'NUMBER' && (
              <ChecklistenEingabe
                type="number"
                inputMode="decimal"
                wert={zahlWert(item.wert)}
                onSpeichern={(v) => onUpdateItem(instanz.id, item.id, v)}
                className={`${gross ? 'w-28' : 'w-24'} shrink-0 px-3 ${feld} rounded border border-dark-border bg-dark-bg text-dark-text`}
                placeholder="Zahl"
                disabled={isCompleted}
              />
            )}
            {item.typ === 'PHOTO' && (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => { /* photo upload */ }}
                className="min-w-0 max-w-full px-3 py-1.5 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className={`break-words [overflow-wrap:anywhere] font-medium ${gross ? 'text-base' : 'text-sm'} ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                {item.text}
                {item.pflicht && <span className="ml-2 text-xs text-red-400">(Pflicht)</span>}
              </p>
              {item.hinweis && <p className={`break-words [overflow-wrap:anywhere] ${gross ? 'text-sm' : 'text-xs'} text-dark-text-muted mt-0.5`}>{item.hinweis}</p>}
              {item.wert && item.ausgefuellt_am && (
                <p className="text-xs text-dark-text-muted mt-1">
                  Ausgefüllt: {new Date(item.ausgefuellt_am).toLocaleString('de-DE')}
                </p>
              )}
            </div>
          </div>
        </ChecklistenZeile>
      ))}

      <div className="pt-4 border-t border-dark-border space-y-2">
        <button
          onClick={() => {
            instanz.items.forEach(item => {
              if (item.typ === 'CHECK' && item.pflicht && (!item.wert || item.wert === '')) {
                onUpdateItem(instanz.id, item.id, 'true')
              }
            })
          }}
          className="w-full min-h-[44px] py-2 px-4 rounded-lg border border-primary-500/60 bg-primary-600/15 text-primary-100 font-medium hover:bg-primary-600/25 transition-colors"
        >
          Alle Pflichtfelder ausfüllen
        </button>
        <button
          onClick={() => onUpdateInstanzStatus(instanz.id, 'ABGESCHLOSSEN')}
          disabled={erledigt < gesamt}
          className="w-full min-h-[44px] py-2 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:bg-primary-600/50 disabled:cursor-not-allowed transition-colors"
        >
          {erledigt === gesamt && gesamt > 0 ? 'Als abgeschlossen markieren' : 'Bitte erst alle Pflichtfelder ausfüllen'}
        </button>
      </div>
    </div>
  )
}

/**
 * Großansicht einer Checkliste: am PC als Pop-up (zentriert, max. 90 % Höhe), auf Smartphones bildschirmfüllend.
 * Bewusst einfache Technik für alle Browser: fixed inset-0 (statt 100vh, das mobile Browser mit Adressleiste
 * falsch berechnen), eigener Scrollbereich, Seite dahinter gesperrt, schließen per X, Esc oder Klick daneben.
 */
function ChecklistenGrossansicht({ instanz, typ, onClose, ...aktionen }: ChecklistenAktionen & {
  instanz: ChecklistenInstanz
  typ?: string
  onClose: () => void
}) {
  useEffect(() => {
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', taste)
    return () => { document.body.style.overflow = vorher; window.removeEventListener('keydown', taste) }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-stretch justify-center bg-black/60 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={checklistenTitel(instanz, typ)}
        className="flex h-full w-full flex-col overflow-hidden bg-dark-bg sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:rounded-2xl sm:border sm:border-dark-border sm:shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-dark-border bg-dark-bg-secondary px-4 py-3">
          <ClipboardList className="h-5 w-5 shrink-0 text-primary-400" />
          <h2 className="min-w-0 flex-1 break-words text-lg font-semibold text-dark-text">{checklistenTitel(instanz, typ)}</h2>
          {/* Mobil eigene Zeile unter dem Titel, damit der Titel nicht mitten im Wort umbricht */}
          <div className="order-last w-full pl-8 sm:order-none sm:w-auto sm:pl-0">
            <ChecklistenFortschritt instanz={instanz} />
          </div>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text"
            aria-label="Schließen"
            title="Schließen"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] [-webkit-overflow-scrolling:touch]">
          <ChecklistenInhalt instanz={instanz} gross {...aktionen} />
        </div>
      </div>
    </div>,
    document.body
  )
}

/** Ohne offene Pflichtpunkte (alle Pflichtpunkte erledigt oder Checkliste abgeschlossen) */
function checklisteFertig(instanz: ChecklistenInstanz | undefined) {
  if (!instanz) return false
  const { erledigt, gesamt } = checklistenStand(instanz)
  return instanz.status === 'ABGESCHLOSSEN' || erledigt >= gesamt
}

function ChecklistenPhaseCard({ 
  instanz, 
  typ,
  onUpdateItem, 
  onUpdateInstanzStatus,
}: ChecklistenAktionen & { 
  instanz: ChecklistenInstanz | undefined
  typ?: string
}) {
  const [gross, setGross] = useState(false)
  const [auf, setAuf] = useState(false)
  const schliessen = useCallback(() => setGross(false), [])

  if (!instanz) {
    return (
      <Card className="min-w-0 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-dark-text-muted" />
            <h3 className="font-semibold text-dark-text-muted">Keine Vorlage</h3>
          </div>
        </div>
        <p className="text-sm text-dark-text-muted text-center py-4">Für diese Phase existiert keine Checklisten-Vorlage.</p>
      </Card>
    )
  }

  const { isComplete, isInProgress } = checklistenStand(instanz)
  const fertig = checklisteFertig(instanz)

  // Karte ohne offene Punkte: eine Zeile (Haken, Titel, erledigt-Zeit), aufklappbar
  if (fertig && !auf) {
    const um = instanz.abgeschlossen_am ? fmtTime(instanz.abgeschlossen_am) : ''
    return (
      <Card className="col-span-full min-w-0" data-testid="checkliste-zusammen">
        <button type="button" aria-expanded={false} onClick={() => setAuf(true)}
          className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left">
          <CheckCircle className="h-4 w-4 shrink-0 text-green-400" />
          <span className="min-w-0 flex-1 truncate font-medium text-dark-text">{checklistenTitel(instanz, typ)}</span>
          <span className="shrink-0 text-xs text-dark-text-muted">{um ? `erledigt ${um}` : 'erledigt'}</span>
          <ChevronDown className="h-5 w-5 shrink-0 text-dark-text-muted" />
        </button>
      </Card>
    )
  }

  // Kein horizontales Scrollen: Karte darf die Grid-Spalte nicht aufweiten, lange Texte brechen um
  return (
    <Card className={`min-w-0 p-4 ${fertig ? 'col-span-full' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div className="flex min-w-0 flex-1 basis-[13rem] items-center gap-2">
          <span className={`w-2 h-2 shrink-0 rounded-full ${isComplete ? 'bg-green-400' : isInProgress ? 'bg-amber-400' : 'bg-red-400'}`} />
          {/* Überschrift öffnet die Großansicht */}
          <button
            type="button"
            onClick={() => setGross(true)}
            className="group flex min-h-[44px] min-w-0 items-center gap-1.5 text-left"
            title="Checkliste groß anzeigen"
          >
            <h3 className="min-w-0 font-semibold text-dark-text [hyphens:none] [overflow-wrap:break-word] group-hover:text-primary-300 group-hover:underline">{checklistenTitel(instanz, typ)}</h3>
            <Maximize2 className="h-4 w-4 shrink-0 text-dark-text-muted group-hover:text-primary-300" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <ChecklistenFortschritt instanz={instanz} />
          {fertig && auf && (
            <button type="button" aria-label="Zusammenklappen" aria-expanded onClick={() => setAuf(false)}
              className="flex h-11 w-11 items-center justify-center rounded text-dark-text-muted hover:text-dark-text">
              <ChevronDown className="h-5 w-5 rotate-180" />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto overflow-x-hidden">
        <ChecklistenInhalt instanz={instanz} onUpdateItem={onUpdateItem} onUpdateInstanzStatus={onUpdateInstanzStatus} />
      </div>

      {gross && (
        <ChecklistenGrossansicht instanz={instanz} typ={typ} onClose={schliessen}
          onUpdateItem={onUpdateItem} onUpdateInstanzStatus={onUpdateInstanzStatus} />
      )}
    </Card>
  )
}

/** 4. Karte: Piloten-Checklisten des Einsatzes (Einsatz-Checklisten des Pilot-Moduls, in Phasen-Reihenfolge).
 *  Alle einzeilig, per Klick aufklappbar. Abgeschlossene: nur lesend. Offene: Punkte abhaken und abschließen. */
function PilotChecklistenKarte({ instanzen, typ, ...aktionen }: ChecklistenAktionen & { instanzen: ChecklistenInstanz[]; typ?: string }) {
  const [aufgeklappt, setAufgeklappt] = useState<number | null>(null)
  return (
    <Card className="min-w-0 p-4" data-testid="pilot-checklisten-karte">
      <div className="mb-3 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 shrink-0 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Piloten-Checklisten</h3>
      </div>
      {instanzen.length === 0 && <p className="py-2 text-sm text-dark-text-muted">Keine Piloten-Checklisten für diesen Einsatz.</p>}
      <div className="space-y-2">
        {[...instanzen].sort((a, b) => Number(checklisteFertig(a)) - Number(checklisteFertig(b))).map(instanz => {
          const { erledigt, gesamt } = checklistenStand(instanz)
          const fertig = instanz.status === 'ABGESCHLOSSEN'
          const um = fertig && instanz.abgeschlossen_am
            ? new Date(instanz.abgeschlossen_am).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''
          const offen = aufgeklappt === instanz.id
          const wer = (instanz.bearbeiter ?? []).join(', ')
          return (
            <div key={instanz.id} className="rounded-lg border border-dark-border bg-dark-bg-tertiary/30" data-testid="pilot-checkliste">
              <button type="button" aria-expanded={offen} onClick={() => setAufgeklappt(offen ? null : instanz.id)}
                className="flex min-h-[44px] w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 px-3 py-2 text-left">
                <div className="flex min-w-0 flex-1 basis-[7rem] items-center gap-2">
                  {fertig
                    ? <CheckCircle className="h-4 w-4 shrink-0 text-green-400" />
                    : <span className={`h-2 w-2 shrink-0 rounded-full ${erledigt > 0 ? 'bg-amber-400' : 'bg-red-400'}`} />}
                  <span className="min-w-0 font-medium text-dark-text [overflow-wrap:break-word]">{checklistenTitel(instanz, typ)}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {fertig
                    ? <Badge variant="success">Abgeschlossen{um ? ` ${um}` : ''}</Badge>
                    : <Badge variant={erledigt > 0 ? 'warning' : 'danger'}>Offen {erledigt}/{gesamt}</Badge>}
                  <ChevronDown className={`h-5 w-5 text-dark-text-muted transition-transform duration-200 ${offen ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {offen && (
                <div className="border-t border-dark-border p-3">
                  {wer && <p className="mb-2 text-xs text-dark-text-muted">abgehakt von {wer}</p>}
                  <div className="max-h-96 overflow-y-auto overflow-x-hidden">
                    <ChecklistenInhalt instanz={instanz} {...aktionen} />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// ============================================================================
// Aktive Flüge (laufende Flüge des Einsatzes, DJI-Live-Telemetrie falls vorhanden)
// ============================================================================

interface AktiverFlug {
  flug_id: number
  flug_nr: string | null
  startzeit: string
  flugart: string | null
  drohne: string | null
  drohne_typ: string | null
  pilot: string | null
  beobachter: string | null
  telemetrie: boolean
  sn: string | null
  alter_s: number | null
  daten: {
    hoehe_m?: number; speed_h?: number; home_distanz_m?: number; akku_prozent?: number; restflugzeit_s?: number
    rth_akku_prozent?: number; gps_satelliten?: number; funk_qualitaet?: number; modus?: string
  }
  rueckkehr: boolean
  steuerung: { nutzer: string | null; verbunden: boolean } | null
  hms: { code: string; stufe: number; meldung: string | null }[]
}

const zahlDe = (v: number | undefined | null, stellen = 0) =>
  v === undefined || v === null ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen })

/** Flugdauer als h:mm:ss bzw. m:ss, zählt live ab Startzeit */
const fmtDauer = (sek: number) => {
  const s = Math.max(0, Math.floor(sek))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`
}

function AktiveFluegeCard({ einsatzId, className }: { einsatzId: string; className?: string }) {
  const navigate = useNavigate()
  const [fluege, setFluege] = useState<AktiverFlug[] | null>(null)
  const [jetzt, setJetzt] = useState(() => Date.now())
  const [ladeFehler, setLadeFehler] = useState(false)
  const holenRef = useRef<() => void>(() => {})
  useConnectionRetry(() => holenRef.current())

  // Telemetrie alle 4 s (nur bei sichtbarem Tab), Flugdauer jede Sekunde
  useEffect(() => {
    let aktiv = true
    const holen = () => {
      if (document.visibilityState !== 'visible') return
      api.get(`/dashboard/einsatz/${einsatzId}/aktive-fluege`)
        .then(r => { if (aktiv) { setFluege((r.data as AktiverFlug[]) ?? []); setLadeFehler(false) } })
        .catch(() => { if (aktiv) setLadeFehler(true) }) // Verbindungsfehler meldet der Interceptor; hier zusätzlich sichtbar an der Karte
    }
    holenRef.current = holen
    setFluege(null)
    setLadeFehler(false)
    holen()
    const t = window.setInterval(holen, 4000)
    return () => { aktiv = false; window.clearInterval(t) }
  }, [einsatzId])
  useEffect(() => {
    if (!fluege?.length) return
    const t = window.setInterval(() => setJetzt(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [fluege?.length])

  const zurKarte = (f: AktiverFlug) => {
    const q = new URLSearchParams({ einsatz: einsatzId })
    if (f.telemetrie && f.sn) q.set('drohne', f.sn)
    navigate(`/taktische-karte?${q.toString()}`)
  }

  return (
    <Card className={`flex h-full max-h-[34rem] flex-col p-4 ${className || ''}`}>
      <div className="mb-4 flex items-center gap-2">
        <PlaneTakeoff className="h-5 w-5 text-green-400" />
        <h3 className="font-semibold text-dark-text">Aktive Flüge ({fluege?.length ?? 0})</h3>
      </div>
      {ladeFehler && (
        <p className="mb-2 rounded bg-red-500/10 px-2 py-1 text-xs text-red-300" role="alert">
          {fluege === null ? 'Aktive Flüge konnten nicht geladen werden.' : 'Aktualisierung fehlgeschlagen – letzte bekannte Daten.'}
        </p>
      )}
      {fluege === null ? (
        <p className="py-8 text-center text-dark-text-muted">{ladeFehler ? 'Wird erneut versucht …' : 'Lade …'}</p>
      ) : fluege.length === 0 ? (
        <p className="py-8 text-center text-dark-text-muted">Keine Drohne in der Luft</p>
      ) : (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
          {fluege.map(f => {
            const d = f.daten || {}
            const veraltet = (f.alter_s ?? 0) > 10
            const akkuKritisch = (d.akku_prozent ?? 100) <= (d.rth_akku_prozent ?? 25) + 5
            const gpsSchwach = (d.gps_satelliten ?? 99) < 8
            const hoch = (d.hoehe_m ?? 0) > 120
            const hmsRot = f.hms.some(h => h.stufe >= 2)
            const dauer = fmtDauer((jetzt - new Date(f.startzeit).getTime()) / 1000)
            const wert = (k: string, v: string, warn: boolean, icon?: React.ReactNode) => (
              <div className="flex items-center justify-between gap-2 rounded bg-dark-bg/40 px-2 py-1">
                <span className="flex items-center gap-1 text-dark-text-muted">{icon}{k}</span>
                <b className={`tabular-nums ${warn ? 'text-red-300' : 'text-dark-text'}`}>{v}</b>
              </div>
            )
            return (
              <button
                key={f.flug_id}
                type="button"
                onClick={() => zurKarte(f)}
                title="Auf der Taktischen Karte anzeigen"
                className="block min-h-[44px] w-full rounded-lg bg-dark-bg-tertiary/50 p-3 text-left text-xs transition-colors hover:bg-dark-bg-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-dark-text">{f.drohne || 'Drohne unbekannt'}</p>
                    <p className="truncate text-dark-text-muted">
                      {f.pilot || 'Pilot unbekannt'}{f.beobachter ? ` · Beobachter ${f.beobachter}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-base font-bold tabular-nums text-green-300">{dauer}</p>
                    {f.flugart && <span className="rounded bg-primary-500/20 px-1.5 py-0.5 text-xs font-semibold text-primary-300">{f.flugart}</span>}
                  </div>
                </div>
                {f.telemetrie ? (
                  <>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {f.rueckkehr && <span className="rounded bg-amber-500 px-1.5 py-0.5 font-semibold text-black">Rückkehr aktiv</span>}
                      {f.steuerung && (
                        <span className={`rounded px-1.5 py-0.5 ${f.steuerung.verbunden ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                          Cloud-Steuerung: {f.steuerung.nutzer || '–'}{f.steuerung.verbunden ? '' : ' (getrennt)'}
                        </span>
                      )}
                      {f.hms.length > 0 && (
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold ${hmsRot ? 'bg-red-600 text-white' : 'bg-amber-500 text-black'}`}>
                          <TriangleAlert className="h-3 w-3" />{f.hms.length} HMS
                        </span>
                      )}
                      {veraltet && <span className="rounded bg-slate-600/60 px-1.5 py-0.5 text-slate-200">Daten {Math.round(f.alter_s ?? 0)} s alt</span>}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1">
                      {wert('Höhe', `${zahlDe(d.hoehe_m)} m`, hoch, <Navigation className="h-3 w-3" />)}
                      {wert('Tempo', `${zahlDe(d.speed_h !== undefined ? d.speed_h * 3.6 : undefined)} km/h`, false)}
                      {wert('Entf. Start', `${zahlDe(d.home_distanz_m)} m`, false)}
                      {wert('Akku', `${zahlDe(d.akku_prozent)} %`, akkuKritisch, <BatteryMedium className="h-3 w-3" />)}
                      {wert('Rest', d.restflugzeit_s !== undefined && d.restflugzeit_s !== null ? `${Math.floor(d.restflugzeit_s / 60)} min` : '–', akkuKritisch)}
                      {wert('GPS', zahlDe(d.gps_satelliten), gpsSchwach, <Satellite className="h-3 w-3" />)}
                      {d.funk_qualitaet !== undefined && d.funk_qualitaet !== null && wert('Signal', `${d.funk_qualitaet}/5`, d.funk_qualitaet <= 1)}
                    </div>
                  </>
                ) : (
                  <p className="mt-2 italic text-dark-text-muted">keine Telemetrie</p>
                )}
              </button>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function FluegeListe({ data, einsatzId }: { data?: FlugListe[]; einsatzId?: string }) {
  // Videoaufnahmen/Ausschnitte je Flug (Livebild-Aufzeichnung): Übersicht laden, Wiedergabe im Fenster
  const [aufnahmen, setAufnahmen] = useState<Record<string, { aufnahmen?: number; ausschnitte?: number }>>({})
  const [offen, setOffen] = useState<FlugListe | null>(null)
  const [aufFehler, setAufFehler] = useState(false)
  useEffect(() => {
    if (!einsatzId) { setAufnahmen({}); setAufFehler(false); return }
    let aktiv = true
    api.get(`/dji/aufnahmen/einsatz/${einsatzId}`)
      .then(r => { if (aktiv) { setAufnahmen(r.data ?? {}); setAufFehler(false) } })
      .catch(() => { if (aktiv) { setAufnahmen({}); setAufFehler(true) } })
    return () => { aktiv = false }
  }, [einsatzId, data?.length])
  return (
    <Card className="h-full p-4">
      <div className="flex items-center gap-2 mb-4">
        <Plane className="w-5 h-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Flüge ({data?.length ?? 0})</h3>
      </div>
      {aufFehler && <p className="mb-2 rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-300" role="alert">Aufnahmen konnten nicht geladen werden.</p>}
      {(data?.length ?? 0) === 0 ? (
        <p className="text-center py-8 text-dark-text-muted">Keine Flüge durchgeführt</p>
      ) : (
        <div className="space-y-2">
          {(data ?? []).map((f, i) => (
            <div key={i} className="py-2 px-3 rounded bg-dark-bg-tertiary/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-400">
                    <Plane className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-dark-text">{f.drohne_label}</p>
                    <p className="text-xs text-dark-text-muted">
                      {f.startzeit ? fmtTime(f.startzeit) : '—'} – {f.endzeit ? fmtTime(f.endzeit) : 'offen'} · {f.dauer_minuten} min
                    </p>
                  </div>
                </div>
                <div className="text-right text-xs text-dark-text-muted">
                  <p>{f.pilot_label}</p>
                  {f.akku_label ? <p>Akku: {f.akku_label}</p> : null}
                  {f.beobachter_label ? <p>Co-Pilot: {f.beobachter_label}</p> : null}
                  {(aufnahmen[String(f.id)]?.aufnahmen || aufnahmen[String(f.id)]?.ausschnitte) ? (
                    <button type="button" onClick={() => setOffen(f)}
                      className="mt-1 min-h-[44px] rounded-lg bg-primary-600 px-3 text-xs font-medium text-white">
                      Aufnahme{aufnahmen[String(f.id)]?.ausschnitte ? ` · ${aufnahmen[String(f.id)]?.ausschnitte} Ausschnitt(e)` : ''}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {offen && createPortal(
        <div className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto bg-black/70 p-2 sm:p-6" onClick={() => setOffen(null)}>
          <div className="w-full max-w-5xl rounded-xl border border-dark-border bg-dark-bg-secondary p-4" onClick={e => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-dark-text">Flug {offen.drohne_label} · {offen.startzeit ? fmtTime(offen.startzeit) : ''}</h3>
              <button type="button" onClick={() => setOffen(null)} className="min-h-[44px] rounded-lg border border-dark-border px-3 text-sm text-dark-text">Schließen</button>
            </div>
            <FlugAufnahme flug={{ id: offen.id }} darfLoeschen />
          </div>
        </div>, document.body)}
    </Card>
  )
}

// ============================================================================
// Main Dashboard
// ============================================================================

export function DashboardPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  // Globale Einsatzwahl (Provider in App.tsx); Kinder erwarten die ID als String
  const { einsatzIdStr: selectedEinsatzId, einsatz: gewaehlterEinsatz, reload: einsatzListeNeu } = useEinsatz()
  const [checklistenFehler, setChecklistenFehler] = useState<string | null>(null)
  // Koordinaten-Fallback aus der Einsatzliste (Rohobjekt der API)
  const aktEinsatz = (gewaehlterEinsatz?.raw ?? null) as EinsatzOption | null
  // laufende Antworten eines früheren Einsatzes verwerfen
  const aktuelleId = useRef(selectedEinsatzId)
  aktuelleId.current = selectedEinsatzId
  const [checklisten, setChecklisten] = useState<ChecklistenInstanz[]>([])
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null)
  const [weatherLoading, setWeatherLoading] = useState(false)
  const [selectedChecklistenInstanz, setSelectedChecklistenInstanz] = useState<ChecklistenInstanz | null>(null)
  const [nurOffen, setNurOffen] = useState(false)
  const [checklistenLoading, setChecklistenLoading] = useState(false)
  const [zeitenOffen, setZeitenOffen] = useState(false)

  const ladenWetter = useCallback(async (lat: number, lon: number) => {
    setWeatherLoading(true)
    try {
      // Server-Endpunkt: Open-Meteo, Ausweichquelle DWD (Bright Sky), zuletzt bekannte Werte; Kp-Index ebenfalls serverseitig
      const res = await api.get('/wetter', { params: { lat, lon } })
      setWeatherData(res.data as WeatherData)
    } catch (e) {
      console.error('Wetter laden fehlgeschlagen:', e)
    } finally {
      setWeatherLoading(false)
    }
  }, [])

  const ladenChecklisten = useCallback(async () => {
    if (!selectedEinsatzId) { setChecklisten([]); return }
    const id = selectedEinsatzId
    setChecklistenLoading(true)
    try {
      const clRes = await api.get(`/einsaetze/${id}/checklisten`)
      if (aktuelleId.current !== id) return
      // Einsatz-Checklisten (flug_id leer): Führung bearbeitbar, Pilot-Modul nur lesend
      setChecklisten(((clRes.data as ChecklistenInstanz[]) || []).filter(c => !c.flug_id))
      setChecklistenFehler(null)
    } catch {
      if (aktuelleId.current === id) setChecklistenFehler('Checklisten konnten nicht geladen werden.')
    } finally {
      setChecklistenLoading(false)
    }
  }, [selectedEinsatzId])

  useEffect(() => { ladenChecklisten() }, [ladenChecklisten])

  /** still = Hintergrund-Aktualisierung ohne Ladeanzeige und ohne erneute Wetterabfrage */
  const laden = useCallback(async (still = false) => {
    if (!selectedEinsatzId) {
      setData(null)
      setWeatherData(null)
      setLoading(false)
      return
    }
    const id = selectedEinsatzId
    if (!still) setLoading(true)
    try {
      const r = await api.get(`/dashboard/einsatz/${id}`)
      if (aktuelleId.current !== id) return
      setFehler(null)
      const raw = (r.data ?? {}) as any
      // Normalize response so every field exists with a safe default
      const einsatzData: DashboardData = {
        einsatz: raw.einsatz ?? { id: 0, titel: '—', typ: '—', status: '—' },
        aktive_einsaetze: raw.aktive_einsaetze ?? [],
        einsatzkraefte: raw.einsatzkraefte ?? { im_einsatz: 0, reserve_wache: 0, einsatzreserve: 0, detail_im_einsatz: [], detail_reserve_wache: [], detail_einsatzreserve: [] },
        piloten: raw.piloten ?? { eingeteilt: 0, verfuegbar: 0, detail_eingeteilt: [], detail_verfuegbar: [] },
        fluege: raw.fluege ?? { anzahl: 0, flugzeit_minuten: 0, flugzeit_label: '0h 0min' },
        geflogene_piloten: raw.geflogene_piloten ?? [],
        einsatzleitung: raw.einsatzleitung ?? { einsatzleiter_typ: null, einsatzleiter_nutzer_id: null, einsatzleiter_name: null, einsatzleiter_funkrufname: null, einsatzleiter_funkgruppe: null },
        einsatzdauer: raw.einsatzdauer ?? { minuten: 0, stunden: 0, minuten_rest: 0, label: '0h 0min' },
        fahrzeuge: raw.fahrzeuge ?? [],
        fluege_liste: raw.fluege_liste ?? [],
      }
      setData(einsatzData)
      // Coordinates: prefer dashboard einsatz object, fallback to aktiveEinsaetze
      const lat = einsatzData.einsatz?.einsatzort_lat ?? aktEinsatz?.einsatzort_lat ?? null
      const lon = einsatzData.einsatz?.einsatzort_lon ?? aktEinsatz?.einsatzort_lon ?? null
      if (lat && lon && !still) {
        ladenWetter(lat, lon)
      }
    } catch (e: any) {
      if (aktuelleId.current !== id) return
      // Hintergrund-Aktualisierung: vorhandene Daten behalten, Fehler zeigt Statusleiste/Banner
      if (!still) setFehler(e?.response?.data?.detail || 'Dashboard-Daten konnten nicht geladen werden')
    } finally {
      if (aktuelleId.current === id) setLoading(false)
    }
  }, [selectedEinsatzId, aktEinsatz])

  // Einsatzwechsel: alten Zustand verwerfen, neu laden
  useEffect(() => { setData(null); setFehler(null); setChecklisten([]); setChecklistenFehler(null); setWeatherData(null) }, [selectedEinsatzId])
  useEffect(() => { laden() }, [laden])
  useConnectionRetry(() => { void laden(); void ladenChecklisten() })

  // Dashboard alle 30 s im Hintergrund aktualisieren (Flüge, Kräfte, Checklisten anderer Nutzer)
  useEffect(() => {
    if (!selectedEinsatzId) return
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      laden(true)
      ladenChecklisten()
    }, 30000)
    return () => window.clearInterval(t)
  }, [selectedEinsatzId, laden, ladenChecklisten])


  // ---- Phasen-Zeitleiste ----
  const phasenSetzen = usePhasenSetzen({
    einsatz: data?.einsatz, holeChecklisten: async () => checklisten,
    nachher: () => Promise.all([laden(true), ladenChecklisten(), einsatzListeNeu()]),
  })
  const phaseBusy = phasenSetzen.busy
  const phaseFehler = phasenSetzen.fehler
  const bestaetigungsDialog = phasenSetzen.dialog
  // Phasenwechsel aus der Kopfzeile (PhasenSteuerung): Dashboard neu laden
  useEffect(() => {
    const f = () => { void laden(true); void ladenChecklisten() }
    window.addEventListener(PHASE_GEAENDERT, f)
    return () => window.removeEventListener(PHASE_GEAENDERT, f)
  }, [laden, ladenChecklisten])
  const einsatzTyp = data?.einsatz?.typ
  const phaseAktuell = aktuellePhase(data?.einsatz)
  const phaseIdx = phaseIndex(phaseAktuell, einsatzTyp)
  const istUebung = einsatzTyp === 'UEBUNG'
  const zeitleisteAktiv = true   // Übungen (UEBUNG) haben keine Phasenleiste (Abschluss über die Einsatzverwaltung)

  const waehlePhase = async (ziel: EinsatzPhaseKey) => { await phasenSetzen.waehle(ziel) }

  // Weicher Hinweis: offene Einsatz-Checklisten der Phasen bis zur aktuellen (Flug-Checklisten zählen nicht)
  const offeneChecklisten = !phaseAktuell || phaseAktuell === 'GEPLANT' || phaseAktuell === 'ABGESCHLOSSEN' ? [] : offeneEinsatzChecklisten(checklisten, { typ: einsatzTyp, bisPhase: phaseAktuell })

  // Karten in fester chronologischer Reihenfolge: Einsatzbereitschaft -> Anfahrt/Briefing -> Einsatzende, dann Piloten-Checklisten
  const FUEHRUNG_REIHE = ['EINSATZBEREITSCHAFT', 'ANFAHRT_BRIEFING', 'EINSATZENDE']
  const PILOT_REIHE = ['EINSATZBEREITSCHAFT', 'EINSATZORT', 'EINSATZENDE_PILOT', 'EINSATZENDE']
  const fuehrungKarten = FUEHRUNG_REIHE.map(ph => checklisten.find(c => c.modul === 'FUEHRUNG' && c.phase === ph))
  const fuehrungOffen = fuehrungKarten.filter(c => !checklisteFertig(c))
  const fuehrungFertig = fuehrungKarten.filter(c => c && checklisteFertig(c))
  const pilotKarten = checklisten.filter(c => c.modul === 'PILOT' && !c.flug_id && PILOT_REIHE.includes(c.phase))
    .sort((a, b) => PILOT_REIHE.indexOf(a.phase) - PILOT_REIHE.indexOf(b.phase) || a.id - b.id)
  const aktualisiereItem = async (instanzId: number, itemId: number, wert: string) => {
    await api.put(`/checklisten/instanzen/${instanzId}/items/${itemId}`, { wert })
    await ladenChecklisten()
  }
  const aktualisiereStatus = async (instanzId: number, newStatus: string) => {
    await api.put(`/checklisten/instanzen/${instanzId}`, { status: newStatus })
    await ladenChecklisten()
  }

  // Weather loading effect - robust coordinate detection (flat data structure)
  useEffect(() => {
    if (!selectedEinsatzId) return
    
    // Try multiple sources for coordinates
    let lat: number | null = null
    let lon: number | null = null
    
    // 1. Try from dashboard einsatz object
    if (data?.einsatz?.einsatzort_lat && data?.einsatz?.einsatzort_lon) {
      lat = data.einsatz.einsatzort_lat
      lon = data.einsatz.einsatzort_lon
    }
    
    // 2. Fallback: try from aktiveEinsaetze
    if (!lat || !lon) {
      if (aktEinsatz?.einsatzort_lat && aktEinsatz?.einsatzort_lon) {
        lat = aktEinsatz.einsatzort_lat
        lon = aktEinsatz.einsatzort_lon
      }
    }
    
    if (lat && lon) {
      ladenWetter(lat, lon)
    }
  }, [selectedEinsatzId, aktEinsatz, data?.einsatz?.einsatzort_lat, data?.einsatz?.einsatzort_lon, ladenWetter])

  if (loading) return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Führungskräfte – Einsatzübersicht" />
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent" />
      </div>
    </div>
  )

  if (fehler) return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Führungskräfte – Einsatzübersicht" />
      <Card className="p-6 border-red-500/30 bg-red-500/10">
        <p className="text-red-200">{fehler}</p>
        <button onClick={() => laden()} className="mt-4 min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Erneut versuchen</button>
      </Card>
    </div>
  )

  if (!selectedEinsatzId) return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Führungskräfte – Einsatzübersicht" />
      <RequireEinsatz>{() => null}</RequireEinsatz>
    </div>
  )

  if (!data) return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Führungskräfte – Einsatzübersicht" />
      <div className="flex flex-col items-center gap-3 py-12" role="status">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        <p className="text-sm text-dark-text-muted">Lade Einsatzdaten …</p>
      </div>
    </div>
  )

  const e = data.einsatz ?? { id: 0, titel: '—', typ: '—', status: '—' }

  return (
    <>
      {bestaetigungsDialog}
      <div className="space-y-6 relative">
      <PageHeader
        title=""
        subtitle="Führungskräfte – Einsatzübersicht"
      />

      {/* === LAGELEISTE (Command Wall): Phase + Einsatzdauer | offene Pflicht-Checklisten | Freigabelink === */}
      <Card className="border-primary-500/30 bg-primary-500/5 px-4 py-2" data-testid="lageleiste">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex min-w-0 items-center gap-4">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-dark-text-muted">Phase</p>
              <p className="truncate text-base font-semibold text-dark-text" data-testid="lage-phase">{phaseAktuell ? phaseLabel(phaseAktuell) : (e.status || '—')}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-dark-text-muted">Einsatzdauer</p>
              <p className="font-mono text-3xl font-bold leading-none tabular-nums text-dark-text" data-testid="lage-dauer">{dauerLabel(data.einsatzdauer)}</p>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="min-w-0 truncate text-sm text-dark-text-muted" title={`${e.titel} · ${e.typ}`}>{e.titel} · {e.typ}</span>
            {!istUebung && (
              <span data-testid="lage-offen-chip"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${offeneChecklisten.length > 0 ? 'border-amber-500/60 bg-amber-500/15 text-amber-200' : 'border-green-500/40 bg-green-500/10 text-green-300'}`}>
                {offeneChecklisten.length > 0 ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                {offeneChecklisten.length > 0 ? `${offeneChecklisten.length} Pflicht-Checkl. offen` : 'Checklisten ok'}
              </span>
            )}
          </div>
          <div className="ml-auto"><FreigabeLinkButton einsatzId={selectedEinsatzId} /></div>
        </div>
      </Card>
      {checklistenFehler && <p className="rounded bg-amber-500/10 px-3 py-2 text-sm text-amber-300" role="alert">{checklistenFehler}</p>}

      {/* === PHASEN-ZEITLEISTE (nur Führungskraft setzt die Phase) === */}
      {!istUebung && <PhasenZeitleiste
        einsatz={e}
        aktuell={phaseAktuell}
        aktiv={zeitleisteAktiv}
        busy={phaseBusy}
        hinweis={null}
        onWaehle={waehlePhase}
        onZeitenBearbeiten={() => setZeitenOffen(true)}
      />}
      {zeitenOffen && data?.einsatz?.id ? (
        <PhasenZeitenDialog einsatz={data.einsatz} api={api} onClose={() => setZeitenOffen(false)} onGespeichert={() => { void laden(true) }} />
      ) : null}
      {/* Ergebnis fehlt bei beendetem/abgeschlossenem Einsatz: nur ein Hinweis; eingetragen wird es in der Einsatzverwaltung (nicht im Dashboard) */}
      {istBeendetOderAbgeschlossen(e.status) && !e.ergebnis && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" role="status" data-testid="ergebnis-fehlt">
          <span className="min-w-0 flex-1">Für diesen Einsatz ist noch kein Ergebnis eingetragen (freiwillig).</span>
          <button type="button" data-testid="ergebnis-eintragen" onClick={() => navigate(`/einsaetze?bearbeiten=${e.id}`)}
            className="min-h-[44px] rounded-lg border border-amber-400 px-4 font-semibold text-amber-100 hover:bg-amber-500/20">Ergebnis in der Einsatzverwaltung eintragen</button>
        </div>
      )}
      {phaseFehler && <p className="rounded bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">{phaseFehler}</p>}
      {offeneChecklisten.length > 0 && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" role="status" data-testid="offene-checklisten">
          <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4 shrink-0" />
            {offeneChecklisten.length === 1 ? 'Noch 1 Checkliste offen' : `Noch ${offeneChecklisten.length} Checklisten offen`}
          </p>
          <ul className="mt-1 list-disc pl-6">{offeneChecklisten.map(o => <li key={o.instanz.id}>{o.text}</li>)}</ul>
        </div>
      )}

      {/* === OBERE REIHE: 4 niedrige Kennzahlen-Kacheln === */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <EinsatzKrafteCard data={data.einsatzkraefte} />
        <PilotenCard data={data.piloten} />
        <FluegeCard data={data.fluege} />
        <EinsatzleitungCard data={data.einsatzleitung} />
      </div>

      {/* === CHECKLISTEN (chronologisch) + PILOTEN-CHECKLISTEN + WETTER === */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-dark-text">Checklisten</h3>
        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-dark-text-muted">
          <input type="checkbox" checked={nurOffen} onChange={e => setNurOffen(e.target.checked)} className="h-4 w-4" data-testid="filter-nur-offen" />
          Nur offen
        </label>
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 [&>*]:min-w-0 items-start">
        {/* Offene Pflichtpunkte zuerst, erledigte als Einzeiler danach */}
        {fuehrungOffen.map((c, i) => (
          <ChecklistenPhaseCard key={c?.id ?? `leer-${i}`} instanz={c} typ={einsatzTyp}
            onUpdateItem={aktualisiereItem} onUpdateInstanzStatus={aktualisiereStatus} />
        ))}
        <PilotChecklistenKarte instanzen={nurOffen ? pilotKarten.filter(c => !checklisteFertig(c)) : pilotKarten} typ={einsatzTyp}
          onUpdateItem={aktualisiereItem} onUpdateInstanzStatus={aktualisiereStatus} />
        <WeatherCard data={weatherData} className="h-full" einsatzId={selectedEinsatzId} />
        {!nurOffen && fuehrungFertig.map(c => (
          <ChecklistenPhaseCard key={c!.id} instanz={c} typ={einsatzTyp}
            onUpdateItem={aktualisiereItem} onUpdateInstanzStatus={aktualisiereStatus} />
        ))}
      </div>

      {/* === UNTERE REIHE: 4 Karten gleich breit === */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 mt-6">
        <GeflogenePilotenCard data={data.geflogene_piloten} />
        <FahrzeugeListe data={data.fahrzeuge} />
        <FluegeListe data={data.fluege_liste} einsatzId={selectedEinsatzId} />
        <AktiveFluegeCard einsatzId={selectedEinsatzId} />
      </div>
    </div>


    </>
  )
}

export default DashboardPage